import { z } from "zod";
import { SlackConfig, SlackConfigCreate, SlackConfigUpdate } from "../schemas/slackConfig";
import { request, validateInput } from "../request";

export class SlackConfigsEndpoint {
  public static readonly basePath = "slack-configs";
  public static readonly schemas = {
    list: {
      query: z.object({
        limit: z.number().int().positive().optional(),
        offset: z.number().int().nonnegative().optional(),
      }),
      response: z.object({ count: z.number().int(), items: SlackConfig.array() }),
    },
    read: { params: z.object({ config_id: z.number().int().positive() }), response: SlackConfig },
    create: { body: SlackConfigCreate, response: SlackConfig },
    update: { body: SlackConfigUpdate, response: SlackConfig },
  };

  public list(query: z.input<typeof SlackConfigsEndpoint.schemas.list.query> = {}) {
    const valid = validateInput(SlackConfigsEndpoint.schemas.list.query, query);
    if (!valid.ok) return Promise.resolve(valid);
    return request({
      log: ["SlackConfigs", "list"],
      method: "get",
      path: [SlackConfigsEndpoint.basePath],
      query: valid.data,
      localErrorHandling: true,
      response: SlackConfigsEndpoint.schemas.list.response,
    });
  }

  public read(configId: number) {
    const valid = validateInput(SlackConfigsEndpoint.schemas.read.params, { config_id: configId });
    if (!valid.ok) return Promise.resolve(valid);
    return request({
      log: ["SlackConfigs", "read"],
      method: "get",
      path: [SlackConfigsEndpoint.basePath, valid.data.config_id],
      localErrorHandling: true,
      response: SlackConfig,
    });
  }

  public create(body: z.input<typeof SlackConfigCreate>) {
    const valid = validateInput(SlackConfigCreate, body);
    if (!valid.ok) return Promise.resolve(valid);
    return request({
      log: ["SlackConfigs", "create"],
      method: "post",
      path: [SlackConfigsEndpoint.basePath],
      json: valid.data,
      localErrorHandling: true,
      response: SlackConfig,
    });
  }

  public update(configId: number, body: z.input<typeof SlackConfigUpdate>) {
    const params = validateInput(SlackConfigsEndpoint.schemas.read.params, { config_id: configId });
    if (!params.ok) return Promise.resolve(params);
    const valid = validateInput(SlackConfigUpdate, body);
    if (!valid.ok) return Promise.resolve(valid);
    return request({
      log: ["SlackConfigs", "update"],
      method: "patch",
      path: [SlackConfigsEndpoint.basePath, params.data.config_id],
      json: valid.data,
      localErrorHandling: true,
      response: SlackConfig,
    });
  }

  public delete(configId: number) {
    const valid = validateInput(SlackConfigsEndpoint.schemas.read.params, { config_id: configId });
    if (!valid.ok) return Promise.resolve(valid);
    return request({
      log: ["SlackConfigs", "delete"],
      method: "delete",
      path: [SlackConfigsEndpoint.basePath, valid.data.config_id],
      localErrorHandling: true,
    });
  }
}
